import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Copy, ArrowRight } from 'lucide-react';
import { Card, Input, Textarea, Button } from '../ui';

export function BylawsTemplatesBuilder() {
  const [orgName, setOrgName] = useState('My Civic Association');
  const [orgPurpose, setOrgPurpose] = useState('To promote community engagement, volunteer gardening, and neighborhood resilience.');
  const [duesCycle, setDuesCycle] = useState('Annual');
  const [copyStatus, setCopyStatus] = useState(false);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopyStatus(true);
    setTimeout(() => setCopyStatus(false), 2000);
  };

  const markdownTemplate = `# Constitution and Bylaws of ${orgName || '[Organization Name]'}

## Article I: Name and Purpose
1. **Name**: The official name of this entity shall be **${orgName || '[Organization Name]'}**.
2. **Purpose**: The purpose of this organization is ${orgPurpose || '[Purpose Statement]'}.

## Article II: Membership & Dues
1. **Eligibility**: Membership is open to all applicants who align with the mission and purposes described herein.
2. **Dues**: Dues are evaluated on an **${duesCycle}** basis and must be cleared to preserve voting rights.

## Article III: Officers & Executive Committee
1. **Roles**: The governing officers will consist of a President, Vice President, and Secretary-Treasurer.
2. **Term**: Officers serve annual terms.
`;

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <Card className="p-6 space-y-4 border-2 border-black dark:border-white border-gray-150">
        <h3 className="text-lg font-bold flex items-center gap-2 mb-2">
          <FileText className="text-purple-500" size={20} /> Document Builder
        </h3>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Organization Name</label>
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Organization Purpose Description</label>
            <Textarea value={orgPurpose} onChange={(e) => setOrgPurpose(e.target.value)} rows={3} />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase font-mono">Dues Payment Interval</label>
            <select
              value={duesCycle}
              onChange={(e) => setDuesCycle(e.target.value)}
              className="w-full h-[40px] px-3 py-2 text-sm border-2 border-black dark:border-white rounded-none focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900"
            >
              <option value="Annual">Annual (Recommended)</option>
              <option value="Semi-Annual">Semi-Annual</option>
              <option value="Quarterly">Quarterly</option>
              <option value="None">No Membership Dues</option>
            </select>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-slate-50 dark:bg-slate-900 border-2 border-black dark:border-white flex flex-col justify-between">
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider font-mono">Bylaws Markdown Output</span>
            <Button size="sm" variant="secondary" onClick={() => handleCopy(markdownTemplate)} className="gap-1 px-2 cursor-pointer border-2 border-black dark:border-white">
              <Copy size={12} /> {copyStatus ? 'Copied!' : 'Copy Markdown'}
            </Button>
          </div>
          <pre className="bg-white dark:bg-slate-950 border-2 border-black dark:border-white p-4 rounded-none font-mono text-[11px] h-[220px] overflow-y-auto leading-relaxed whitespace-pre-wrap">
            {markdownTemplate}
          </pre>
        </div>
        <div className="mt-4 pt-3 border-t-2 border-black dark:border-white">
          <div className="p-4 bg-[#FFD600] text-black border-2 border-black dark:border-white space-y-2">
            <p className="text-xs font-bold leading-snug">
              Done drafting? Publish these bylaws into an encrypted OrgSets Vault and cascade them down to all your chapters with 1-click member acknowledgment tracking.
            </p>
            <Link
              to="/sign-up"
              className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider bg-black text-white hover:bg-[#0044FF] px-3 py-1.5 transition-colors cursor-pointer"
            >
              Save to Your Org Vault <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
